import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';

// Layout
import MainLayout from './components/layout/MainLayout';

// Pages
import Dashboard from './pages/Dashboard';
import DayPage from './pages/DayPage';
import JournalPage from './pages/JournalPage';
import MusicPage from './pages/MusicPage';
import TalksPage from './pages/TalksPage';
import ProfilePage from './pages/ProfilePage';
import ExamenPage from './pages/ExamenPage';
import RIMPPage from './pages/RIMPPage';
import DiscernmentPage from './pages/DiscernmentPage';
import AboutPage from './pages/AboutPage';
import ProgressPage from './pages/ProgressPage';
import ResourcesPage from './pages/ResourcesPage';

// Components
import SelfDiscoveryProfileModal from './components/retreat/modals/SelfDiscoveryProfileModal';
import OnboardingFlow from './components/OnboardingFlow';
import WelcomeBackModal, { shouldShowWelcome } from './components/WelcomeBackModal';
import NotificationSettingsModal from './components/NotificationSettingsModal';
import WeeklySummaryModal from './components/WeeklySummaryModal';
import AccountabilityPartnerModal from './components/AccountabilityPartnerModal';
import WhatsNewModal from './components/WhatsNewModal';

// Storage keys
const NOTES_STORAGE_KEY = 'dmu-rimp-retreat-notes';
const RETREAT_START_KEY = 'dmu-rimp-retreat-start';
const DARK_MODE_KEY = 'dmu-rimp-retreat-dark-mode';
const BOOKMARKS_STORAGE_KEY = 'dmu-rimp-retreat-bookmarks';
const PROFILE_STORAGE_KEY = 'dmu-rimp-retreat-profile';
const ONBOARDING_KEY = 'dmu-rimp-retreat-onboarded';

// Helper to parse date string
const parseDateString = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

// Helper to calculate current retreat day
const calculateCurrentDay = (startDate) => {
  if (!startDate) return null;
  const start = parseDateString(startDate);
  if (!start) return null;
  
  const today = new Date();
  const todayNoon = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0, 0);
  
  const diffTime = todayNoon.getTime() - start.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;
  
  if (diffDays < 1) return 0;
  if (diffDays > 30) return 31;
  return diffDays;
};

function App() {
  // State
  const [dailyNotes, setDailyNotes] = useState({});
  const [retreatStartDate, setRetreatStartDate] = useState('');
  const [currentRetreatDay, setCurrentRetreatDay] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [bookmarkedDays, setBookmarkedDays] = useState({});
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [openProfileWithSync, setOpenProfileWithSync] = useState(false);
  const [isRetreatStartOpen, setIsRetreatStartOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const [showWeeklySummary, setShowWeeklySummary] = useState(false);
  const [showPartner, setShowPartner] = useState(false);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [selfDiscoveryProfile, setSelfDiscoveryProfile] = useState({
    prayerStyles: '',
    spiritualGifts: '',
    temperament: '',
    personality: '',
    learningStyle: '',
    naturalTalents: '',
    missionField: '',
    professionalCharism: '',
    passionAbilityNeed: '',
    lifeVerse: '',
    patronSaints: '',
    legacyStatement: ''
  });

  // Load data from localStorage on mount
  useEffect(() => {
    try {
      // Check if user has completed onboarding
      const hasOnboarded = localStorage.getItem(ONBOARDING_KEY);
      if (!hasOnboarded) {
        setShowOnboarding(true);
      }

      // Load notes
      const savedNotes = localStorage.getItem(NOTES_STORAGE_KEY);
      if (savedNotes) {
        setDailyNotes(JSON.parse(savedNotes));
      }

      // Load retreat start date
      const savedStart = localStorage.getItem(RETREAT_START_KEY);
      if (savedStart) {
        setRetreatStartDate(savedStart);
        setCurrentRetreatDay(calculateCurrentDay(savedStart));
      }

      // Load dark mode
      const savedDarkMode = localStorage.getItem(DARK_MODE_KEY);
      if (savedDarkMode) {
        setDarkMode(JSON.parse(savedDarkMode));
      }

      // Load bookmarks
      const savedBookmarks = localStorage.getItem(BOOKMARKS_STORAGE_KEY);
      if (savedBookmarks) {
        setBookmarkedDays(JSON.parse(savedBookmarks));
      }

      // Load profile
      const savedProfile = localStorage.getItem(PROFILE_STORAGE_KEY);
      if (savedProfile) {
        setSelfDiscoveryProfile(prev => ({ ...prev, ...JSON.parse(savedProfile) }));
      }

      // Check if we should show What's New modal (for existing users who haven't seen v2.0)
      const hasExistingData = localStorage.getItem(RETREAT_START_KEY) || localStorage.getItem(NOTES_STORAGE_KEY);
      const seenWhatsNew = localStorage.getItem('dmu-rimp-whats-new-seen');
      if (hasExistingData && seenWhatsNew !== '2.0') {
        setShowWhatsNew(true);
      } else {
        // Only show Welcome Back if What's New isn't showing
        const startDateLoaded = localStorage.getItem(RETREAT_START_KEY);
        if (startDateLoaded) {
          const day = calculateCurrentDay(startDateLoaded);
          if (shouldShowWelcome(day)) {
            setShowWelcome(true);
          }
        }
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    }
  }, []);

  // Save note
  const handleSaveNote = (dayNum, text) => {
    const newNotes = { ...dailyNotes, [dayNum]: text };
    setDailyNotes(newNotes);
    localStorage.setItem(NOTES_STORAGE_KEY, JSON.stringify(newNotes));
  };

  // Erase note
  const handleEraseNote = (dayNum) => {
    const newNotes = { ...dailyNotes };
    delete newNotes[dayNum];
    setDailyNotes(newNotes);
    localStorage.setItem(NOTES_STORAGE_KEY, JSON.stringify(newNotes));
  };

  // Toggle bookmark
  const handleToggleBookmark = (dayNum) => {
    const newBookmarks = { ...bookmarkedDays, [dayNum]: !bookmarkedDays[dayNum] };
    setBookmarkedDays(newBookmarks);
    localStorage.setItem(BOOKMARKS_STORAGE_KEY, JSON.stringify(newBookmarks));
  };

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem(DARK_MODE_KEY, JSON.stringify(newMode));
  };

  // Set retreat start date
  const handleSetStartDate = (date) => {
    setRetreatStartDate(date);
    setCurrentRetreatDay(calculateCurrentDay(date));
    localStorage.setItem(RETREAT_START_KEY, date);
    setIsRetreatStartOpen(false);
  };

  // Open sync panel
  const handleOpenSync = () => {
    setOpenProfileWithSync(true);
    setIsProfileModalOpen(true);
  };

  // Update profile
  const updateProfile = (field, value) => {
    const newProfile = { ...selfDiscoveryProfile, [field]: value };
    setSelfDiscoveryProfile(newProfile);
    localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(newProfile));
  };

  // Sync complete handler
  const handleSyncComplete = (data) => {
    if (data.notes && Object.keys(data.notes).length > 0) {
      setDailyNotes(data.notes);
      localStorage.setItem(NOTES_STORAGE_KEY, JSON.stringify(data.notes));
    }
    if (data.bookmarks && Object.keys(data.bookmarks).length > 0) {
      setBookmarkedDays(data.bookmarks);
      localStorage.setItem(BOOKMARKS_STORAGE_KEY, JSON.stringify(data.bookmarks));
    }
    if (data.retreatStartDate) {
      setRetreatStartDate(data.retreatStartDate);
      setCurrentRetreatDay(calculateCurrentDay(data.retreatStartDate));
      localStorage.setItem(RETREAT_START_KEY, data.retreatStartDate);
    }
  };

  // Complete onboarding
  const handleOnboardingComplete = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setShowOnboarding(false);
  };

  // Show onboarding for new users
  if (showOnboarding) {
    return (
      <OnboardingFlow 
        onComplete={handleOnboardingComplete}
        onSetStartDate={handleSetStartDate}
      />
    );
  }

  return (
    <BrowserRouter>
      <MainLayout
        currentDay={currentRetreatDay}
        darkMode={darkMode}
        onToggleDarkMode={toggleDarkMode}
      >
        <Routes>
          <Route 
            path="/" 
            element={
              <Dashboard 
                currentDay={currentRetreatDay}
                dailyNotes={dailyNotes}
                retreatStartDate={retreatStartDate}
                onSetStartDate={() => setIsRetreatStartOpen(true)}
              />
            } 
          />
          <Route 
            path="/day/:dayNumber" 
            element={
              <DayPage 
                dailyNotes={dailyNotes}
                onSaveNote={handleSaveNote}
                onEraseNote={handleEraseNote}
                bookmarkedDays={bookmarkedDays}
                onToggleBookmark={handleToggleBookmark}
              />
            } 
          />
          <Route 
            path="/journal" 
            element={
              <JournalPage 
                dailyNotes={dailyNotes}
                currentDay={currentRetreatDay}
              />
            } 
          />
          <Route path="/music" element={<MusicPage />} />
          <Route path="/talks" element={<TalksPage />} />
          <Route 
            path="/profile" 
            element={
              <ProfilePage 
                currentDay={currentRetreatDay}
                retreatStartDate={retreatStartDate}
                dailyNotes={dailyNotes}
                bookmarkedDays={bookmarkedDays}
                darkMode={darkMode}
                onToggleDarkMode={toggleDarkMode}
                onOpenSync={handleOpenSync}
                onOpenRetreatStart={() => setIsRetreatStartOpen(true)}
                onOpenProfile={() => setIsProfileModalOpen(true)}
                onOpenReminder={() => setShowReminder(true)}
                onOpenWeeklySummary={() => setShowWeeklySummary(true)}
                onOpenPartner={() => setShowPartner(true)}
              />
            } 
          />
          {/* Reference pages */}
          <Route path="/reference/examen" element={<ExamenPage />} />
          <Route path="/reference/rimp" element={<RIMPPage />} />
          <Route path="/reference/discernment" element={<DiscernmentPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route 
            path="/progress" 
            element={
              <ProgressPage 
                currentDay={currentRetreatDay}
                dailyNotes={dailyNotes}
                bookmarkedDays={bookmarkedDays}
                retreatStartDate={retreatStartDate}
              />
            } 
          />
        </Routes>
      </MainLayout>

      {/* Profile Modal */}
      <SelfDiscoveryProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => {
          setIsProfileModalOpen(false);
          setOpenProfileWithSync(false);
        }}
        profile={selfDiscoveryProfile}
        onUpdateProfile={updateProfile}
        dailyNotes={dailyNotes}
        bookmarkedDays={bookmarkedDays}
        retreatStartDate={retreatStartDate}
        openSyncPanel={openProfileWithSync}
        onSyncComplete={handleSyncComplete}
      />

      {/* Retreat Start Date Modal - Simple implementation */}
      {isRetreatStartOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <h2 className="font-heading text-xl font-bold text-[#5D1F2F] mb-4">Set Start Date</h2>
            <p className="text-gray-600 text-sm mb-4">When would you like to begin your 30-day retreat?</p>
            <input
              type="date"
              className="w-full p-3 border border-gray-200 rounded-lg mb-4"
              value={retreatStartDate}
              onChange={(e) => setRetreatStartDate(e.target.value)}
            />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  handleSetStartDate(today);
                }}
                className="flex-1 bg-[#C5A059] text-white py-2 rounded-lg font-medium"
              >
                Start Today
              </button>
              <button
                onClick={() => handleSetStartDate(retreatStartDate)}
                disabled={!retreatStartDate}
                className="flex-1 bg-[#5D1F2F] text-white py-2 rounded-lg font-medium disabled:opacity-50"
              >
                Save Date
              </button>
            </div>
            <button
              onClick={() => setIsRetreatStartOpen(false)}
              className="w-full mt-2 text-gray-500 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Welcome Back Modal */}
      {showWelcome && currentRetreatDay && currentRetreatDay >= 1 && currentRetreatDay <= 30 && (
        <WelcomeBackModal
          currentDay={currentRetreatDay}
          dailyNotes={dailyNotes}
          onClose={() => setShowWelcome(false)}
        />
      )}

      {/* Notification Settings Modal */}
      <NotificationSettingsModal
        isOpen={showReminder}
        onClose={() => setShowReminder(false)}
        currentDay={currentRetreatDay}
      />

      {/* Weekly Summary Modal */}
      <WeeklySummaryModal
        isOpen={showWeeklySummary}
        onClose={() => setShowWeeklySummary(false)}
        dailyNotes={dailyNotes}
        currentDay={currentRetreatDay}
        retreatStartDate={retreatStartDate}
      />

      {/* Accountability Partner Modal */}
      <AccountabilityPartnerModal
        isOpen={showPartner}
        onClose={() => setShowPartner(false)}
        currentDay={currentRetreatDay}
        dailyNotes={dailyNotes}
        retreatStartDate={retreatStartDate}
      />

      {/* What's New Modal - for returning users */}
      {showWhatsNew && (
        <WhatsNewModal onClose={() => {
          setShowWhatsNew(false);
          // After dismissing What's New, check if Welcome Back should show
          if (shouldShowWelcome(currentRetreatDay)) {
            setShowWelcome(true);
          }
        }} />
      )}
    </BrowserRouter>
  );
}

export default App;
